import { useRef } from 'react';
import PropTypes from 'prop-types';

// styles
import {
    CardDisplayLargeContainer,
    CardImageFrame,
    CardTilt,
    CardTiltSurface,
    ContentContainer,
    ImgContainer,
    LeftOverlayContent,
    RightOverlayContent
} from './CardDisplayLarge.styles';

// assets
import pullVaultIcon from '../../../../assets/system/cardvault-symbol.png';

// components
import CardAttribute from '../../../../components/card-attribute/CardAttribute';
import RevealImage from '../../../../components/reveal-image/RevealImage';

const MAX_TILT = 12;

function tiltCard(event, surface) {
    if (!surface) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (event.target.closest('button, select, input, a')) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    const rotateX = py * MAX_TILT * 2;
    const rotateY = px * MAX_TILT * -2;
    surface.style.transition = 'transform 70ms linear';
    surface.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
}

function resetTilt(surface) {
    if (!surface) return;
    surface.style.transition = 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)';
    surface.style.transform = 'rotateX(0deg) rotateY(0deg)';
}

export default function CardDisplayLarge({ cardData, overlay = null, footer = null }) {
    const surfaceRef = useRef(null);

    // Early return if no card data
    if (!cardData) {
        return null;
    }
    
    const {
        name = [],
        team = [],
        cardNumber = "",
        cardAttributes = [],
        cardParallel = {},
        imageURL = null,
        imageOrientation = "vertical",
        cardPath = []
    } = cardData;
    
    // Use imageURL from cardData
    const displayImage = imageURL;
    // Use imageOrientation from cardData
    const displayOrientation = imageOrientation;
    const hasImage = !!displayImage;

    // Extract series information from cardPath
    // cardPath format: /checklists/sport/year/manufacturer/series/cardType/cardTypeCategory
    const getSeriesInfo = () => {
        if (!cardPath || cardPath.length === 0) return null;
        
        // Find the first path that contains the series information
        const pathString = Array.isArray(cardPath) ? cardPath[0] : cardPath;
        if (!pathString) return null;
        
        const pathSegments = pathString.replace('/checklists/', '').split('/').filter(Boolean);
        if (pathSegments.length >= 4) {
            const [sport, year, manufacturer, series] = pathSegments;
            return { sport, year, manufacturer, series };
        }
        return null;
    };

    const seriesInfo = getSeriesInfo();

    return (
        <CardDisplayLargeContainer orientation={displayOrientation}>
            <CardTilt
                onMouseMove={(event) => tiltCard(event, surfaceRef.current)}
                onMouseLeave={() => resetTilt(surfaceRef.current)}
            >
                <CardTiltSurface ref={surfaceRef}>
                    <ContentContainer $orientation={displayOrientation}>
                        <LeftOverlayContent $orientation={displayOrientation}>
                            {name.map((playerName) => {
                                return <h1 key={playerName}>{playerName.toUpperCase()}</h1>
                            })}
                            {team.map((teamName) => {
                                return <CardAttribute key={teamName} size='L' text={teamName} />
                            })}
                        </LeftOverlayContent>
                        <CardImageFrame>
                            <ImgContainer orientation={displayOrientation} $hasUploadedImage={!!displayImage}>
                                {hasImage ? (
                                    <RevealImage
                                        key={displayImage}
                                        src={displayImage}
                                        alt="card image"
                                        style={{ height: '100%', width: '100%' }}
                                    />
                                ) : (
                                    <img
                                        src={pullVaultIcon}
                                        alt="no card"
                                    />
                                )}
                            </ImgContainer>
                            {overlay}
                        </CardImageFrame>
                        <RightOverlayContent $orientation={displayOrientation}>
                            {seriesInfo && (
                                <CardAttribute
                                    text={`${seriesInfo.year} ${seriesInfo.manufacturer} ${seriesInfo.series}`}
                                    size='L'
                                />
                            )}
                            <CardAttribute text={cardNumber} size='L' />
                            {cardAttributes.length !== 0 &&
                                <CardAttribute text={cardAttributes} size='L' />
                            }
                            <CardAttribute color={cardParallel.cardColor} text={cardParallel.cardPrintNumber ? `${cardParallel.cardParallelName} (${cardParallel.cardPrintNumber}/${cardParallel.cardSupply})` : cardParallel.cardParallelName} size={'L'} />
                        </RightOverlayContent>
                    </ContentContainer>
                    {footer}
                </CardTiltSurface>
            </CardTilt>
        </CardDisplayLargeContainer>
    )
}

CardDisplayLarge.propTypes = {
    cardData: PropTypes.object.isRequired
};
